import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { submitLeadWithGeoLocation, getCountryInfo } from '../services/centralLeadApi';
import countryCodes from '../data/countryCodes';
import { toast } from 'sonner';
import { getServiceData } from '../data/allServicesData';
import SDHeroSection from '../components/sd/SDHeroSection';
import SDMobileContactForm from '../components/sd/SDMobileContactForm';
import SDWhatIsSection from '../components/sd/SDWhatIsSection';
import SDProcessSection from '../components/sd/SDProcessSection';
import SDPricingSection from '../components/sd/SDPricingSection';
import SDBenefitsSection from '../components/sd/SDBenefitsSection';
import SDTestimonialsSlider from '../components/sd/SDTestimonialsSlider';
// import SDCTASection from '../components/sd/SDCTASection';

const ServiceDetail = ({ onOpenModal }) => {
  const { category, serviceSlug } = useParams();
  const content = getServiceData(serviceSlug, category) || {
    category: 'IP Services',
    title: 'Professional IP Services',
    subtitle: 'Expert Legal Protection',
    hero: 'Comprehensive intellectual property protection services.',
    whatIsTitle: 'What is this service?',
    whatIsContent: 'Professional legal services to protect your intellectual property.',
    keyFeatures: [],
    benefits: [],
    pricing: [],
    process: []
  };

  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [errors, setErrors] = useState({ email: '', phone: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [countryCode, setCountryCode] = useState('+1');
  const [detectedCountry, setDetectedCountry] = useState(null);

  useEffect(() => {
    const fetchGeo = async () => {
      try {
        const info = await getCountryInfo();
        if (info) {
          setDetectedCountry(info);
          const match = countryCodes.find(c => c.country === info.countryCode);
          if (match) setCountryCode(match.code);
        }
      } catch (e) { console.error('Geo fetch error:', e); }
    };
    fetchGeo();
  }, []);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => phone.replace(/[^\d]/g, '').length === 10;

  const handleEmailBlur = () => {
    if (formData.email && !validateEmail(formData.email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address (e.g., user@example.com)' }));
    } else {
      setErrors(prev => ({ ...prev, email: '' }));
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^0-9\s\-]/g, '');
    const digits = value.replace(/[^\d]/g, '');
    if (digits.length > 10) return;
    setFormData(prev => ({ ...prev, phone: value }));
    if (errors.phone && digits.length === 10) setErrors(prev => ({ ...prev, phone: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let hasErrors = false;
    const newErrors = { email: '', phone: '' };

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address (e.g., user@example.com)';
      hasErrors = true;
    }
    if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Phone number must be exactly 10 digits (e.g., 123-456-7890)';
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      toast.error('Please fix the errors before submitting');
      return;
    }

    setIsLoading(true);
    try {
      const leadData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        countryCode: countryCode,
        country: countryCodes.find(c => c.code === countryCode)?.country || 'US',
        description: formData.message || `Service inquiry: ${content?.title || serviceSlug}`
      };

      const result = await submitLeadWithGeoLocation(leadData, null, `Service Page: ${content?.title || serviceSlug}`);
      
      if (result.success) {
        toast.success(result.message || 'Request submitted successfully!');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setErrors({ email: '', phone: '' });
      } else {
        toast.error(result.message || 'Submission failed. Please try again.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to submit. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-16">
      <SDHeroSection content={content} serviceSlug={serviceSlug} />
      <SDMobileContactForm
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        countryCode={countryCode}
        setCountryCode={setCountryCode}
        countryCodes={countryCodes}
        detectedCountry={detectedCountry}
        isLoading={isLoading}
        handleSubmit={handleSubmit}
        handleEmailBlur={handleEmailBlur}
        handlePhoneChange={handlePhoneChange}
        validateEmail={validateEmail}
      />
      <SDWhatIsSection content={content} serviceSlug={serviceSlug} onOpenModal={onOpenModal} />
      <SDProcessSection content={content} />
      <SDPricingSection content={content} onOpenModal={onOpenModal} />
      <SDBenefitsSection content={content} onOpenModal={onOpenModal} serviceSlug={serviceSlug} />
      <SDTestimonialsSlider />
      {/* <SDCTASection content={content} onOpenModal={onOpenModal} /> */}
    </div>
  );
};

export default ServiceDetail;
